import { Link } from "@inertiajs/react";
import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Mail, KeyRound, UserMinus, UserPlus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import { useToast } from "@/hooks/useToast";
import {
  activateTeamMember,
  deactivateTeamMember,
  fetchManageableTeams,
  fetchTeamMembers,
  inviteTeamMember,
  resendTeamMemberInvitation,
  sendTeamMemberPasswordReset,
  type TeamManagementMember,
  type TeamManagementTeam,
} from "@/api/teamManagement";

export type TeamManagementProps = {
  passwordReset?: boolean;
  embedded?: boolean;
  teamManagementOnly?: boolean;
};

function statusBadge(status: TeamManagementMember["status"]) {
  if (status === "active") return <Badge variant="secondary">Active</Badge>;
  if (status === "invited") return <Badge variant="outline">Invited</Badge>;
  return <Badge variant="destructive">Deactivated</Badge>;
}

function Shell({ embedded, children }: { embedded?: boolean; children: ReactNode }) {
  if (embedded) {
    return (
      <div className="space-y-4" data-testid="users-settings">
        {children}
      </div>
    );
  }

  return (
    <div className={moduleWorkspaceShellClass} data-testid="team-management">
      {children}
    </div>
  );
}

export function TeamManagement({
  passwordReset = true,
  embedded = false,
  teamManagementOnly = false,
}: TeamManagementProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const teamsQuery = useQuery({
    queryKey: ["teamManagement", "teams", { teamManagementOnly }],
    queryFn: () =>
      teamManagementOnly ? fetchManageableTeams({ teamManagementOnly: true }) : fetchManageableTeams(),
  });

  const activeTeamId = selectedTeamId ?? teamsQuery.data?.[0]?.id ?? null;

  const membersQuery = useQuery({
    queryKey: ["teamManagement", "members", activeTeamId],
    queryFn: () => fetchTeamMembers(activeTeamId!),
    enabled: activeTeamId != null,
  });

  const selectedTeam = useMemo(
    () => teamsQuery.data?.find((team) => team.id === activeTeamId),
    [teamsQuery.data, activeTeamId],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["teamManagement"] });
  };

  const inviteMutation = useMutation({
    mutationFn: (email: string) => inviteTeamMember(activeTeamId!, email),
    onSuccess: (data) => {
      toast({ title: data.message });
      setInviteOpen(false);
      setInviteEmail("");
      invalidate();
    },
    onError: (error: Error) => {
      toast({ title: error.message, variant: "destructive" });
    },
  });

  const actionMutation = useMutation({
    mutationFn: async ({
      userId,
      action,
    }: {
      userId: number;
      action: "deactivate" | "activate" | "send_password_reset" | "resend_invitation";
    }) => {
      if (activeTeamId == null) return;
      switch (action) {
        case "deactivate":
          return deactivateTeamMember(activeTeamId, userId);
        case "activate":
          return activateTeamMember(activeTeamId, userId);
        case "send_password_reset":
          return sendTeamMemberPasswordReset(activeTeamId, userId);
        case "resend_invitation":
          return resendTeamMemberInvitation(activeTeamId, userId);
      }
    },
    onSuccess: (_data, variables) => {
      const labels = {
        deactivate: "Member updated",
        activate: "Member reactivated",
        send_password_reset: "Password reset email sent",
        resend_invitation: "Invitation resent",
      };
      toast({ title: labels[variables.action] });
      invalidate();
    },
    onError: (error: Error) => {
      toast({ title: error.message, variant: "destructive" });
    },
  });

  const actionsDisabled = actionMutation.isPending;
  const toolbar = (
    <TeamToolbar
      teams={teamsQuery.data}
      activeTeamId={activeTeamId}
      onSelectTeam={setSelectedTeamId}
      onInvite={() => setInviteOpen(true)}
      inviteDisabled={activeTeamId == null || inviteMutation.isPending}
    />
  );

  if (teamsQuery.isLoading) {
    return (
      <Shell embedded={embedded}>
        {embedded ? (
          <p className="text-sm text-muted-foreground">Loading teams…</p>
        ) : (
          <ModuleWorkspaceBody width="wide">
            <p className="text-sm text-muted-foreground">Loading teams…</p>
          </ModuleWorkspaceBody>
        )}
      </Shell>
    );
  }

  if (teamsQuery.isError) {
    return (
      <Shell embedded={embedded}>
        {embedded ? (
          <p className="text-sm text-destructive">Could not load teams. Please refresh and try again.</p>
        ) : (
          <ModuleWorkspaceBody width="wide">
            <p className="text-sm text-destructive">Could not load teams. Please refresh and try again.</p>
          </ModuleWorkspaceBody>
        )}
      </Shell>
    );
  }

  if ((teamsQuery.data?.length ?? 0) === 0) {
    return (
      <Shell embedded={embedded}>
        {!embedded && (
          <ModuleWorkspaceHeader
            title="Team Management"
            subtitle="Invite teammates and help them get access to Ordinal."
            leading={
              <div className="rounded-lg bg-primary/10 p-2">
                <Users className="h-6 w-6 text-primary" aria-hidden />
              </div>
            }
          />
        )}
        {embedded ? (
          <p className="text-sm text-muted-foreground" data-testid="team-management-empty">
            You no longer have permission to manage any teams.
          </p>
        ) : (
          <ModuleWorkspaceBody width="wide">
            <div className="space-y-3 rounded-lg border p-6" data-testid="team-management-empty">
              <p className="text-sm text-muted-foreground">
                You no longer have permission to manage any teams.
              </p>
              <Link href="/" className="text-sm font-medium text-primary hover:underline">
                Back to Ordinal
              </Link>
            </div>
          </ModuleWorkspaceBody>
        )}
      </Shell>
    );
  }

  const roster = (
    <>
      {membersQuery.isError && (
        <p className="text-sm text-destructive" data-testid="team-management-roster-error">
          Could not load the team roster. Please try again.
        </p>
      )}
      {membersQuery.isLoading && <p className="text-sm text-muted-foreground">Loading team roster…</p>}
      {membersQuery.data && (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Terms</TableHead>
                <TableHead>Access</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {membersQuery.data.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">{member.email}</TableCell>
                  <TableCell>{statusBadge(member.status)}</TableCell>
                  <TableCell>{member.terms_accepted ? "Accepted" : "Pending"}</TableCell>
                  <TableCell>
                    {member.can_manage_users ? "Can manage team" : embedded ? "User" : "Member"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      {member.invitation_pending && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={actionsDisabled}
                          onClick={() =>
                            actionMutation.mutate({ userId: member.id, action: "resend_invitation" })
                          }
                        >
                          <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden />
                          Resend invite
                        </Button>
                      )}
                      {passwordReset && !member.discarded && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={actionsDisabled}
                          onClick={() =>
                            actionMutation.mutate({
                              userId: member.id,
                              action: "send_password_reset",
                            })
                          }
                        >
                          <KeyRound className="mr-1 h-3.5 w-3.5" aria-hidden />
                          Reset password
                        </Button>
                      )}
                      {member.discarded ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={actionsDisabled}
                          onClick={() =>
                            actionMutation.mutate({ userId: member.id, action: "activate" })
                          }
                        >
                          Reactivate
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={actionsDisabled}
                          onClick={() =>
                            actionMutation.mutate({ userId: member.id, action: "deactivate" })
                          }
                        >
                          <UserMinus className="mr-1 h-3.5 w-3.5" aria-hidden />
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );

  return (
    <Shell embedded={embedded}>
      {embedded ? (
        toolbar
      ) : (
        <ModuleWorkspaceHeader
          title="Team Management"
          subtitle="Invite teammates and help them get access to Ordinal."
          leading={
            <div className="rounded-lg bg-primary/10 p-2">
              <Users className="h-6 w-6 text-primary" aria-hidden />
            </div>
          }
          actions={toolbar}
          summary={
            selectedTeam
              ? {
                  stats: [
                    { label: "Team", value: selectedTeam.name },
                    {
                      label: "Members",
                      value: membersQuery.data?.length ?? "—",
                      hint: "People on this team",
                    },
                  ],
                  loading: membersQuery.isLoading,
                  testId: "team-management-summary",
                }
              : undefined
          }
        />
      )}

      {embedded ? roster : <ModuleWorkspaceBody width="wide">{roster}</ModuleWorkspaceBody>}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite teammate</DialogTitle>
            <DialogDescription>
              We will email them an invitation to join {selectedTeam?.name ?? "your team"}.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              inviteMutation.mutate(inviteEmail.trim());
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="name@city.gov"
                required
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={inviteMutation.isPending || !inviteEmail.trim()}>
                <Mail className="mr-2 h-4 w-4" aria-hidden />
                Send invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function TeamToolbar({
  teams,
  activeTeamId,
  onSelectTeam,
  onInvite,
  inviteDisabled,
}: {
  teams: TeamManagementTeam[] | undefined;
  activeTeamId: number | null;
  onSelectTeam: (teamId: number) => void;
  onInvite: () => void;
  inviteDisabled: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {teams && teams.length > 1 && activeTeamId != null && (
        <div className="min-w-[220px] space-y-1">
          <Label htmlFor="team-management-team">Team</Label>
          <Select value={String(activeTeamId)} onValueChange={(value) => onSelectTeam(Number(value))}>
            <SelectTrigger id="team-management-team">
              <SelectValue placeholder="Select a team" />
            </SelectTrigger>
            <SelectContent>
              {teams.map((team) => (
                <SelectItem key={team.id} value={String(team.id)}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <Button type="button" onClick={onInvite} disabled={inviteDisabled}>
        <UserPlus className="mr-2 h-4 w-4" aria-hidden />
        Invite teammate
      </Button>
    </div>
  );
}
